import axios, { AxiosResponse } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'semantic-ui-css/semantic.min.css';
import { Dimmer, Loader } from 'semantic-ui-react';
import LicenseError from './components/Admin/LicenseError';
import ErrorPage from './components/ErrorPage/ErrorPage';
import Footer from './components/Layout/Footer';
import Layout from './components/Layout/Layout';
import NavMenuBasic from './components/Layout/NavMenuBasic';
import { SettingsContext } from "./contexts/SettingsContext";
import { config } from './contexts/UrlContext';
import './custom.css';
import NoLayoutRouter from './components/Layout/iFrameLayout/NoLayoutRouter';
import { AnyARecord } from 'dns';
import AuthContext from './contexts/AuthContext';
import { getRequest } from './services/axiosService';

function App() {

    const [isLoading, setIsLoading] = useState(true);
    const [isLicensed, setLicensed] = useState(false);
    const [username, setUserName] = useState("");
    const [admin, setAdmin] = useState(false);
    const [adminPrivileges, setAdminPrivileges] = useState(false);
    const [statsPrivileges, setStatsPrivileges] = useState(false);
    const [basicPrivileges, setBasicPrivileges] = useState(false);
    const [currentUser, setCurrentUser] = useState("");
    const [validCMConn, setValidCMConn] = useState(false);
    const [error, setError] = useState("");
    const [isGridDisplay, setGridDisplay] = useState(false);

    const { auth } = useContext(AuthContext);

    useEffect(() => {
    async function getData() {
        setIsLoading(true);
        let location = window.location.href;
        let isDisplay: any = location && location.toString().toLowerCase().includes("/grid?");
        setGridDisplay(isDisplay);

        let isLicensed = await checkLicense();
        let validCMConn = await checkCMConnection();

        if (isLicensed && validCMConn) {
            await checkIsAdmin();
            await checkAdminPrivileges();
            await checkStatsPrivileges();
            await checkBasicPrivileges();
            await getCurrentUser();
        }

        setIsLoading(false);
      
    }
    getData();
    }, [username]);

    const checkLicense = async () => {

        try {
            let response: AxiosResponse = await getRequest(auth, "/ContentManager/CheckLicense");

            setLicensed(response.data);
            return response.data;
        } catch(error) {
            let e: any = error;
            setLicensed(false);
            console.log(e.response);
            toast.error("An error occurred, please check logs");
            return false;
        }
    }

    const checkCMConnection = async () => {
        try {
            let response: AxiosResponse = await getRequest(auth, `/ContentManager/CheckCMConnection?username=${username}`);
            setValidCMConn(true);
            return true;
        } catch(error) {
            let e: any = error;
            setError(e);
            toast.error("Error connecting to Content Manager")
            setValidCMConn(false);
            return false;
        }
    }

    const checkIsAdmin = async () => {
        try {
            let response: AxiosResponse = await getRequest(auth, `/ContentManager/CheckStrictlyAdmin`);
            setAdmin(response.data);
        } catch (error) {
            toast.error("Error checking admin status")
            setAdmin(false);
        }
    }
    const getCurrentUser = async () => {
        try {
            let response: AxiosResponse = await getRequest(auth, `/ContentManager/GetCurrentUser`);
            setCurrentUser(response.data);
        } catch(error) {
            toast.error("Error checking current user")
        }
    }

    const checkAdminPrivileges = async () => {
        try {
            let response: AxiosResponse = await getRequest(auth, `/ContentManager/CheckAdmin`);
            setAdminPrivileges(response.data);
        } catch (error) {
            toast.error("Error checking admin status")
            setAdminPrivileges(false);
        }
    }

    const checkBasicPrivileges = async () => {
        try {
            let response: AxiosResponse = await getRequest(auth, `/ContentManager/CheckBasic`);
            setBasicPrivileges(response.data);
        } catch (error) {
            toast.error("Error checking basic status")
            setBasicPrivileges(false);
        }
    }

    const checkStatsPrivileges = async () => {
        try {
            let response: AxiosResponse = await getRequest(auth, `/ContentManager/CheckStatsRights`);
            setStatsPrivileges(response.data);
        } catch (error) {
            toast.error("Error checking statistics status")
            setStatsPrivileges(false);
        }
    }

    if (isLoading) {
        return (
            <Dimmer inverted active={true}>
            <Loader inverted>Loading CM2WebShare...</Loader>
            </Dimmer>
        );
    } 

    if (validCMConn) {
        if (isLicensed) {
            if (isGridDisplay) {
                return (
                    <SettingsContext.Provider
                        value={{
                            isAdmin: admin, 
                            hasAdminPrivileges: adminPrivileges,
                            currentUser: currentUser,
                            hasStatsPrivileges: statsPrivileges,  
                            hasBasicPrivileges: basicPrivileges
                        }}    
                >
                        <NoLayoutRouter />
                </SettingsContext.Provider>
                    );
            }
            else if (adminPrivileges || statsPrivileges || basicPrivileges) {
                return (
                    <SettingsContext.Provider
                        value={{
                            isAdmin: admin,
                            hasAdminPrivileges: adminPrivileges,
                            currentUser: currentUser,
                            hasStatsPrivileges: statsPrivileges,
                            hasBasicPrivileges: basicPrivileges
                        }}
                    >
                        <Router>
                            <Layout />
                        </Router>
                    </SettingsContext.Provider>
                );
            }
            else {
                return (
                    <SettingsContext.Provider
                        value={{
                            isAdmin: admin,
                            hasAdminPrivileges: adminPrivileges,
                            currentUser: currentUser,
                            hasStatsPrivileges: statsPrivileges,
                            hasBasicPrivileges: basicPrivileges
                        }}
                    >
                        <NavMenuBasic />
                        <ErrorPage message="Only Administrators, Records Managers or whitelisted users can access CM2WebShare" title="Access Denied" />
                        <div className="site-footer">
                            <Footer />
                        </div>
                    </SettingsContext.Provider>
                );
            }
        
    } else {
        return (
            <SettingsContext.Provider
                value={{
                    isAdmin: admin,
                    hasAdminPrivileges: adminPrivileges,
                    currentUser: currentUser,
                    hasStatsPrivileges: statsPrivileges,
                    hasBasicPrivileges: basicPrivileges
                }}
            >
            <LicenseError />
            </SettingsContext.Provider>
        );
    } 
    } else {
        return (
            <SettingsContext.Provider
                value={{
                    isAdmin: admin,
                    hasAdminPrivileges: adminPrivileges,
                    currentUser: currentUser,
                    hasStatsPrivileges: statsPrivileges,
                    hasBasicPrivileges: basicPrivileges
                }}
            >
                <NavMenuBasic/>
                <ErrorPage message={error} title="Error" />
                <div className="site-footer">
                    <Footer/>
                </div>
            </SettingsContext.Provider>
        );
    }
    }

export default App;